<template>
  <div>
    <div>
      <aha-modal
        @hide="hide()"
        :title="modalTitle"
        :action-position="onlyRootOrNone(folders) ? 'justify-end' : 'justify-between'"
        :size="isPersonalWorkingMode ? 'md' : 'sm'"
      >
        <div v-if="loading" class="text-center">
          <aha-spinner />
        </div>

        <div v-if="!loading && onlyRootOrNone(folders)">
          {{ $i18nPresenter.t("There’s no folder to move to. Please create a new folder first.") }}
        </div>

        <div class="max-h-48" v-if="!onlyRootOrNone(folders)">
          <aha-radio
            class="folder-list__radio-btn"
            :key="folder.folderId"
            v-for="folder in folders"
            :value="folder.folderId"
            v-model="destinatedFolderId"
            :class="[{
              'disabled': folder.disabled,
              'ml-8': !isPersonalWorkingMode && folder.folderId,
              'hidden': folder.depth && folder.depth > 10
            }, `ml-${folder.depth + 5 * folder.depth}`]"
            :disabled="folder.disabled"
            v-tooltip="(folder.countPresentation && folder.countPresentation === maxPresentationInFolder
              && folder.depth !== 0) ? maxPresentationFolderTooltip : null"
          >
            <span class="folder-list__icon-circle">
              <aha-icon name="folder" class="folder-list__icon" />
            </span>
            <span
              class="folder-list__folder-name"
              :class="{'pr-1': folder.folderName === 'My presentations'}"
            >
              {{ folder.folderName }}
            </span>
          </aha-radio>
        </div>

        <template #action>
          <div name="additional" v-if="!onlyRootOrNone(folders)">
            <div
              class="flex items-center font-bold cursor-pointer text-primary-90"
              :class="{'hidden': isNewFolderButtonHidden}"
              @click="handleShowModalCreateNewFolder()"
            >
              <aha-icon
                name="add"
                height="18"
                width="18"
                viewBox="0 0 24 24"
              />
              <p class="pl-2">
                {{ $i18nPresenter.t("New Folder") }}
              </p>
            </div>
          </div>
          <div class="flex items-center">
            <aha-link @click="hide()">
              <span class="font-bold">{{ $i18nPresenter.t("Cancel") }}</span>
            </aha-link>
            <aha-button
              :disabled="isMovingPresn"
              @click="handleMovePresn()"
              v-if="!onlyRootOrNone(folders)"
              icon-name="move"
              :submitting="isMovingPresn"
              has-margin-left
            >
              <aha-spinner variant="white" v-if="isMovingPresn" class="mr-1" />
              {{ $i18nPresenter.t("Move") }}
            </aha-button>
            <aha-button
              @click="handleShowModalCreateNewFolder()"
              v-if="onlyRootOrNone(folders)"
              has-margin-left
            >
              {{ $i18nPresenter.t("Create Folder") }}
            </aha-button>
          </div>
        </template>
      </aha-modal>
    </div>
    <!-- Need to pass presentationIds array to this modal -->
    <modal-create-new-folder
      :presentationIds="selectedPresentationIds"
      v-if="isModalCreateNewFolderShowing"
      @hide="hide()"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

// modals
import ModalCreateNewFolder from '@/components-v2/modals/CreateNewFolder.vue'

// api
import PresentationApi from '@/api/presentation'
import FolderApi from '@/api/folder'

// utils
import Utils from '@/util'
import configs from '@/config'
import EventBus from '@/eventBus'

// constants
import eventCategories from '@/constant/eventCategories'

const { MAX_FOLDER_DEPTH, MAX_PRESENTATION_IN_FOLDER } = configs

export default {
  components: {
    ModalCreateNewFolder,
  },
  props: {
    selectedPresentations: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isModalCreateNewFolderShowing: false,
      isMovingPresn: false,
      modalTitle: '',
      selectedPresentationIds: [],
      destinatedFolderId: null,
      folders: [],
      loading: false,
      maxPresentationInFolder: MAX_PRESENTATION_IN_FOLDER,
    }
  },
  computed: {
    ...mapState({
      workingMode: (state) => state.workingMode,
      currentFolderDepth: (state) => state.currentFolderDepth,
    }),
    isAllFoldersDisabled() {
      return this.folders.every((f) => f.disabled === true)
    },
    isPersonalWorkingMode() {
      return this.workingMode === 'personal'
    },
    isNewFolderButtonHidden() {
      return this.$route.name === 'FolderDetail' && this.currentFolderDepth === MAX_FOLDER_DEPTH
    },
    maxPresentationFolderTooltip() {
      return `Maximum presentations reached (${MAX_PRESENTATION_IN_FOLDER}/${MAX_PRESENTATION_IN_FOLDER})`
    },
  },
  async created() {
    this.generateModalTitle()

    this.loading = true
    await this.getAndSetFolders()

    if (this.selectedPresentations.length) {
      this.selectedPresentationIds = this.selectedPresentations.map((presn) => presn.id)
    }
    this.loading = false
  },
  methods: {
    ...mapActions(['setFoldersAction', 'getFoldersAction']),
    onlyRootOrNone(f) {
      return !f.length || !f.some((p) => p.folderId != null)
    },
    trackEvent(label, value) {
      Utils.mixpanelTracking(
        eventCategories.PRESENTATION_LIST,
        label,
        value,
      )
    },
    generateModalTitle() {
      const selectedPresnLength = this.selectedPresentations.length
      if (selectedPresnLength === 1) {
        this.modalTitle = this.$i18nPresenter.t('Move this presentation to…')
      } else if (selectedPresnLength > 1) {
        const title = this.$i18nPresenter.t('moveXPresentationsTo', {
          length: selectedPresnLength,
        })
        this.modalTitle = title
      }
    },
    async getAndSetFolders() {
      const folderTree = await FolderApi.getFullFolderTree()
      const flatFolderTree = this._flattenFolderTree(folderTree)

      flatFolderTree.unshift({ folderId: null, folderName: this.$i18nPresenter.t('myPresentations') })
      this.folders = flatFolderTree.map((f) => {
        return {
          ...f,
          disabled: this.selectedPresentations.some((p) => (p.folderId === f.folderId)
            || (f.countPresentation === MAX_PRESENTATION_IN_FOLDER && f.depth && f.depth > 0)),
          folderName: this.selectedPresentations.some((p) => p.folderId === f.folderId) ? `${f.folderName} (current)` : f.folderName,
        }
      })
      const enabled = this.folders.filter((f) => !f.disabled)
      this.destinatedFolderId = enabled && enabled[0] && enabled[0].folderId
    },
    _flattenFolderTree(folderTree) {
      return folderTree.reduce((acc, {
        folderId, folderName, parentFolderId, depth, folders, countPresentation,
      }) => {
        acc.push({
          folderId, folderName, parentFolderId, depth, countPresentation,
        })
        if (folders && folders.length > 0) {
          acc = acc.concat(this._flattenFolderTree(folders))
        }
        return acc
      }, [])
    },
    handleBeforeCallAPIMovePresn() {
      this.isMovingPresn = true
      if (this.allFoldersDisabled) {
        Utils.showToastError(this.$i18nPresenter.t('Move presentation(s) failed: No folder selected'))
        this.isMovingPresn = false
        return
      }
      this.trackEvent('Presentation List > Move Presentation > Submit')
    },
    handleAfterCallAPIMovePresn() {
      this.isMovingPresn = false
      EventBus.$emit('reload-presentations-and-folders')

      this.handleShowToastSuccess()
      this.trackEvent('Presentation List > Move Presentation > Succeeded')
      this.hide()
    },
    handleShowModalCreateNewFolder() {
      this.isModalCreateNewFolderShowing = true
    },
    async handleMovePresn() {
      this.handleBeforeCallAPIMovePresn()
      try {
        const data = {
          presentationIds: this.selectedPresentationIds,
          presentation: { folderId: this.destinatedFolderId },
        }

        await PresentationApi.patchPresentations(data)
        this.handleAfterCallAPIMovePresn()
      } catch (error) {
        this.isMovingPresn = false
        Utils.showToastError(this.$i18nPresenter.t('Move presentations failed'))
        this.trackEvent(
          'Presentation List > Move Presentation > Failed',
          error.body ? error.body.message : '',
        )
      }
    },
    handleShowToastSuccess() {
      let folderName = 'My presentations'

      if (this.destinatedFolderId) {
        folderName = this.folders
          .find((folder) => folder.folderId === this.destinatedFolderId).folderName
      }

      if (this.selectedPresentationIds && this.selectedPresentationIds.length === 1) {
        Utils.showToastSuccess(`"${this.selectedPresentations[0].name}" ${this.$i18nPresenter.t('moved to')} "${folderName}"`)
      } else if (this.selectedPresentationIds && this.selectedPresentationIds.length > 1) {
        Utils.showToastSuccess(`${this.selectedPresentationIds.length} ${this.$i18nPresenter.t('presentations moved to')} "${folderName}"`)
      }
    },
    hide() {
      this.$emit('hide')
    },
  },
}
</script>

<style lang="scss" scoped>
.folder-list__radio-btn {
  @apply pr-2 mb-2;
  .folder-list__icon-circle {
    @apply rounded-full bg-purple-10 w-max p-2;
    .folder-list__icon {
      @apply text-purple-50;
    }
  }
  .folder-list__folder-name {
    @apply pl-2 overflow-hidden overflow-ellipsis;
  }
  &.disabled {
    &.radio-component {
      @apply cursor-not-allowed;
    }
    .folder-list__icon-circle {
    @apply bg-base-10;
    .folder-list__icon {
      @apply text-base-60;
    }
  }
    .folder-list__folder-name {
      @apply text-base-80
    }
  }
}
.ml-12 {
  margin-left: 3rem;
}
.ml-18 {
  margin-left: 4.5rem;
}
.ml-24 {
  margin-left: 6rem;
}
.ml-30 {
  margin-left: 7.5rem;
}
.ml-36 {
  margin-left: 9rem;
}
.ml-42 {
  margin-left: 10.5rem;
}
.ml-48 {
  margin-left: 12rem;
}
.ml-54 {
  margin-left: 13.5rem;
}
.ml-60 {
  margin-left: 15rem;
}
</style>
